/**
 * FilterPanel — attribute and capability filter controls for the Repo_List_View
 * (Req 5, Req 6).
 *
 * Renders the active {@link FilterState} as a set of controlled inputs and
 * reports every change through `onChange` with a brand-new `FilterState` (the
 * component never mutates the prop). The filtering *semantics* live entirely in
 * the pure `filterSummaries` (lib/query.ts); this panel is purely the selection
 * surface that drives that state, so it is trivially testable.
 *
 * Dimensions surfaced here:
 *  - Attribute multi-selects, OR-ed within a dimension and AND-ed across
 *    dimensions by the query layer (Req 5.6, 5.7):
 *      - status — the fixed set {success, partial, failed} (Req 5.1).
 *      - language — values supplied by the caller (Req 5.2).
 *      - visibility — values supplied by the caller (Req 5.3).
 *      - private — the booleans true/false (Req 5.4).
 *      - archived — the booleans true/false (Req 5.5).
 *  - The eight Capability_Signal filters, each a tri-state off / present /
 *    absent control (Req 6.9, 6.10). "off" removes the key from
 *    `filters.capabilities` (inactive); "present"/"absent" set the mode.
 *
 * A clear-all control resets to the empty `FilterState` so the list shows every
 * Repo_Result subject only to an active search term (Req 5.10, 5.11, 6.11).
 *
 * The component is presentational and controlled: it owns no filter state of
 * its own. Selection state is held by the parent (the App / query-state hook)
 * and threaded back in via `filters`.
 */

import type {
  CapabilityFilterMode,
  CapabilityKey,
  FilterState,
  ScanStatus,
} from "../types";
import type {
  ClassificationDimension,
  ClassificationFilterState,
  ClassificationOptions,
} from "../lib/libraryFilter";
import {
  CLASSIFICATION_DIMENSIONS,
  EMPTY_CLASSIFICATION_FILTERS,
} from "../lib/libraryFilter";

// ---------------------------------------------------------------------------
// Static option metadata
// ---------------------------------------------------------------------------

/** The fixed Status set (Req 5.1) in display order. */
const STATUS_OPTIONS: readonly ScanStatus[] = ["success", "partial", "failed"];

/** Human-readable labels for each Status option. */
const STATUS_LABELS: Record<ScanStatus, string> = {
  success: "Success",
  partial: "Partial",
  failed: "Failed",
};

/**
 * The eight Capability_Signal dimensions in display order, with the labels used
 * for their controls (Req 6.1-6.8).
 */
const CAPABILITY_OPTIONS: readonly { key: CapabilityKey; label: string }[] = [
  { key: "hasCicd", label: "CI/CD" },
  { key: "hasDockerfile", label: "Dockerfile" },
  { key: "hasIac", label: "Infrastructure as code" },
  { key: "hasApiSpec", label: "API specification" },
  { key: "hasReadme", label: "README" },
  { key: "hasOwner", label: "Owner" },
  { key: "hasDomainMatches", label: "Domain matches" },
  { key: "hasErrors", label: "Errors" },
];

/** Tri-state values for a single capability control. */
type CapabilityControlValue = "off" | CapabilityFilterMode;

/**
 * ADDITIVE (Req 18.1, 18.6): display labels for the five Library classification
 * facets, in display order. These render ONLY when the parent supplies
 * `classificationOptions` (a Library document exists for the run).
 */
const CLASSIFICATION_LABELS: Record<ClassificationDimension, string> = {
  repoType: "Repo type",
  lifecycleStatus: "Lifecycle status",
  owningTeam: "Owning team",
  application: "Application",
  product: "Product",
};

/** The empty filter state used by the clear-all control (Req 5.10). */
const EMPTY_FILTERS: FilterState = {
  status: [],
  language: [],
  visibility: [],
  private: [],
  archived: [],
  capabilities: {},
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface FilterPanelProps {
  /** The active filter selections (controlled). */
  filters: FilterState;
  /** Called with the next `FilterState` whenever a control changes. */
  onChange: (next: FilterState) => void;
  /**
   * Selectable primary-language values for the language dimension (Req 5.2),
   * typically derived from the loaded summaries. Defaults to none.
   */
  availableLanguages?: string[];
  /**
   * Selectable visibility values for the visibility dimension (Req 5.3),
   * typically derived from the loaded summaries. Defaults to none.
   */
  availableVisibilities?: string[];
  /**
   * ADDITIVE (Req 18.1, 18.6): the selectable option lists for the five Library
   * classification facets, derived by the parent from the loaded Library
   * document. When omitted (`undefined`) — an older run with no Library document
   * — NO classification facets are rendered and the panel is identical to
   * today's (graceful absence). A dimension with an empty option list is also
   * hidden.
   */
  classificationOptions?: ClassificationOptions;
  /**
   * ADDITIVE (Req 18.1): the active classification-facet selections (controlled).
   * Defaults to the empty selection. Modeled separately from `FilterState` so
   * the existing query types/functions are untouched.
   */
  classificationFilters?: ClassificationFilterState;
  /**
   * ADDITIVE (Req 18.1): called with the next `ClassificationFilterState`
   * whenever a classification facet changes. Omitted when there is no Library
   * document, in which case no facets render and this is never invoked.
   */
  onClassificationChange?: (next: ClassificationFilterState) => void;
}

// ---------------------------------------------------------------------------
// Pure state transitions (no mutation of the incoming FilterState)
// ---------------------------------------------------------------------------

/**
 * Toggle membership of `value` in a string/Status array dimension, returning a
 * new array. Selecting adds the value; deselecting removes every occurrence.
 */
function toggleInArray<T>(values: readonly T[], value: T, selected: boolean): T[] {
  if (selected) {
    return values.includes(value) ? [...values] : [...values, value];
  }
  return values.filter((entry) => entry !== value);
}

/** True when the boolean `value` is present in an active boolean dimension. */
function booleanSelected(values: readonly boolean[], value: boolean): boolean {
  return values.includes(value);
}

/** Read the tri-state control value for a capability key from the filter state. */
function capabilityValue(
  capabilities: FilterState["capabilities"],
  key: CapabilityKey,
): CapabilityControlValue {
  return capabilities[key] ?? "off";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Controlled attribute + capability filter panel. Emits a fresh `FilterState`
 * on every change and never mutates its `filters` prop.
 */
export default function FilterPanel({
  filters,
  onChange,
  availableLanguages = [],
  availableVisibilities = [],
  classificationOptions,
  classificationFilters = EMPTY_CLASSIFICATION_FILTERS,
  onClassificationChange,
}: FilterPanelProps) {
  // --- Attribute: status (Req 5.1) ---
  const onToggleStatus = (status: ScanStatus, selected: boolean) => {
    onChange({ ...filters, status: toggleInArray(filters.status, status, selected) });
  };

  // --- Attribute: language (Req 5.2) ---
  const onToggleLanguage = (language: string, selected: boolean) => {
    onChange({
      ...filters,
      language: toggleInArray(filters.language, language, selected),
    });
  };

  // --- Attribute: visibility (Req 5.3) ---
  const onToggleVisibility = (visibility: string, selected: boolean) => {
    onChange({
      ...filters,
      visibility: toggleInArray(filters.visibility, visibility, selected),
    });
  };

  // --- Attribute: private (Req 5.4) ---
  const onTogglePrivate = (value: boolean, selected: boolean) => {
    onChange({
      ...filters,
      private: toggleInArray(filters.private, value, selected),
    });
  };

  // --- Attribute: archived (Req 5.5) ---
  const onToggleArchived = (value: boolean, selected: boolean) => {
    onChange({
      ...filters,
      archived: toggleInArray(filters.archived, value, selected),
    });
  };

  // --- Capability tri-state (Req 6.9, 6.10) ---
  const onChangeCapability = (key: CapabilityKey, value: CapabilityControlValue) => {
    const capabilities = { ...filters.capabilities };
    if (value === "off") {
      delete capabilities[key]; // inactive — drop the key (Req 6.10)
    } else {
      capabilities[key] = value; // "present" | "absent" (Req 6.9)
    }
    onChange({ ...filters, capabilities });
  };

  // --- Clear all (Req 5.10) ---
  const onClearAll = () => {
    onChange({ ...EMPTY_FILTERS });
  };

  // --- Classification facets (Req 18.1, 18.6) — ADDITIVE ---
  const onToggleClassification = (
    dimension: ClassificationDimension,
    value: string,
    selected: boolean,
  ) => {
    if (!onClassificationChange) return;
    onClassificationChange({
      ...classificationFilters,
      [dimension]: toggleInArray(
        classificationFilters[dimension],
        value,
        selected,
      ),
    });
  };

  // The classification facets render only when a Library document exists for
  // the run (the parent passes `classificationOptions`). Within that, a
  // dimension is shown only when it has at least one option value.
  const classificationDimensions: ClassificationDimension[] = classificationOptions
    ? CLASSIFICATION_DIMENSIONS.filter(
        (dimension) => classificationOptions[dimension].length > 0,
      )
    : [];

  return (
    <section aria-label="Filters" className="ds-panel ds-panel--filters">
      <header className="ds-panel__header">
        <h2 className="ds-panel__title">Filters</h2>
        <button type="button" className="ds-btn ds-btn--tertiary" onClick={onClearAll}>
          Clear all filters
        </button>
      </header>

      {/* Capability dropdown grid (Req 6.9, 6.10) — uppercase labels above
          native selects, laid out as a two-column grid to match the reference
          design. This is the primary filter surface. */}
      <div className="ds-filter-grid" role="group" aria-label="Capabilities">
        {CAPABILITY_OPTIONS.map(({ key, label }) => {
          const value = capabilityValue(filters.capabilities, key);
          const selectId = `capability-${key}`;
          const isActive = value !== "off";
          const cellClass = [
            "ds-filter-grid__cell",
            isActive ? "ds-filter-grid__cell--active" : null,
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <div key={key} className={cellClass}>
              <label htmlFor={selectId} className="ds-filter-grid__label">
                {label}
              </label>
              <select
                id={selectId}
                className="ds-select"
                value={value}
                onChange={(event) =>
                  onChangeCapability(
                    key,
                    event.target.value as CapabilityControlValue,
                  )
                }
              >
                <option value="off">Not set</option>
                <option value="present">Present</option>
                <option value="absent">Absent</option>
              </select>
            </div>
          );
        })}
      </div>

      {/* Status (Req 5.1) */}
      <fieldset className="ds-fieldset">
        <legend>Status</legend>
        {STATUS_OPTIONS.map((status) => (
          <label key={status} className="ds-check">
            <input
              type="checkbox"
              checked={filters.status.includes(status)}
              onChange={(event) => onToggleStatus(status, event.target.checked)}
            />
            {STATUS_LABELS[status]}
          </label>
        ))}
      </fieldset>

      {/* Language (Req 5.2) */}
      <fieldset className="ds-fieldset">
        <legend>Language</legend>
        {availableLanguages.length === 0 ? (
          <p>No languages available.</p>
        ) : (
          availableLanguages.map((language) => (
            <label key={language} className="ds-check">
              <input
                type="checkbox"
                checked={filters.language.includes(language)}
                onChange={(event) =>
                  onToggleLanguage(language, event.target.checked)
                }
              />
              {language}
            </label>
          ))
        )}
      </fieldset>

      {/* Visibility (Req 5.3) */}
      <fieldset className="ds-fieldset">
        <legend>Visibility</legend>
        {availableVisibilities.length === 0 ? (
          <p>No visibilities available.</p>
        ) : (
          availableVisibilities.map((visibility) => (
            <label key={visibility} className="ds-check">
              <input
                type="checkbox"
                checked={filters.visibility.includes(visibility)}
                onChange={(event) =>
                  onToggleVisibility(visibility, event.target.checked)
                }
              />
              {visibility}
            </label>
          ))
        )}
      </fieldset>

      {/* Private (Req 5.4) */}
      <fieldset className="ds-fieldset">
        <legend>Private</legend>
        <label className="ds-check">
          <input
            type="checkbox"
            checked={booleanSelected(filters.private, true)}
            onChange={(event) => onTogglePrivate(true, event.target.checked)}
          />
          Private
        </label>
        <label className="ds-check">
          <input
            type="checkbox"
            checked={booleanSelected(filters.private, false)}
            onChange={(event) => onTogglePrivate(false, event.target.checked)}
          />
          Public
        </label>
      </fieldset>

      {/* Archived (Req 5.5) */}
      <fieldset className="ds-fieldset">
        <legend>Archived</legend>
        <label className="ds-check">
          <input
            type="checkbox"
            checked={booleanSelected(filters.archived, true)}
            onChange={(event) => onToggleArchived(true, event.target.checked)}
          />
          Archived
        </label>
        <label className="ds-check">
          <input
            type="checkbox"
            checked={booleanSelected(filters.archived, false)}
            onChange={(event) => onToggleArchived(false, event.target.checked)}
          />
          Not archived
        </label>
      </fieldset>

      {/*
        Classification facets (Req 18.1, 18.6) — ADDITIVE. Rendered only when a
        Library document exists for the run AND the dimension has option values;
        otherwise nothing renders here and the panel is identical to today's
        (graceful absence). Each facet is a multi-select OR-ed within the
        dimension and AND-ed across dimensions by `filterByClassification`, and
        exposes the explicit `"unknown"` marker value as a selectable option.
      */}
      {classificationOptions &&
        classificationDimensions.map((dimension) => (
          <fieldset key={dimension} className="ds-fieldset">
            <legend>{CLASSIFICATION_LABELS[dimension]}</legend>
            {classificationOptions[dimension].map((value) => (
              <label key={value} className="ds-check">
                <input
                  type="checkbox"
                  checked={classificationFilters[dimension].includes(value)}
                  onChange={(event) =>
                    onToggleClassification(dimension, value, event.target.checked)
                  }
                />
                {value}
              </label>
            ))}
          </fieldset>
        ))}
    </section>
  );
}
